import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Tag, FileText, Lightbulb, Shield, Radio, BarChart3 } from "lucide-react";
import { motion } from "framer-motion";

// Insights/Articles content
const insights = [
  {
    id: "counter-uas-simulation-assessment",
    title: "Why Simulation Matters for Counter-UAS Capability Assessment",
    excerpt: "Live testing alone cannot explore the full parameter space of counter-UAS engagements. Simulation provides the statistical foundation needed for confident procurement and deployment decisions.",
    category: "Defence",
    date: "2025-06-10",
    readTime: "6 min read",
    icon: Shield,
    featured: true,
    content: {
      sections: [
        {
          heading: "The Limits of Live Testing",
          text: "Counter-UAS programmes face a fundamental challenge: live testing is expensive, time-constrained, and produces limited data points. A typical test campaign might generate dozens of intercept attempts. But procurement decisions and operational doctrine require confidence across hundreds of scenarios—different threat profiles, environmental conditions, and engagement geometries."
        },
        {
          heading: "Statistical Confidence from Simulation",
          text: "Simulation allows teams to run thousands of intercept scenarios with controlled variance. Rather than relying on a handful of successful demonstrations, decision-makers can understand probability distributions, edge cases, and failure modes before committing resources."
        },
        {
          heading: "Model Governance and Provenance",
          text: "Simulation is only useful if the results can be trusted and explained. That requires clear model provenance: what inputs drive the simulation, what confidence levels apply to each parameter, and what limitations bound the results. Every RLK simulation output traces back to specific model libraries and seed values."
        },
        {
          heading: "From Simulation to Live Test",
          text: "Simulation does not replace live testing—it prioritises it. By understanding the parameter space through simulation, teams can focus live test resources on the scenarios that matter most: the edge cases, the failure modes, the conditions where the system performs at its limits."
        }
      ],
      relatedPlatform: { name: "UAVSimNet", href: "https://c-uas.co.uk", external: true }
    }
  },
  {
    id: "gps-denied-autonomy-design-principles",
    title: "Designing Autonomy for GPS-Denied Environments",
    excerpt: "GPS denial is not an edge case—it's the baseline assumption for contested operations. Vision-based guidance architectures must be designed for independence, not as a fallback when satellite navigation fails.",
    category: "Technology",
    date: "2025-06-05",
    readTime: "5 min read",
    icon: Lightbulb,
    featured: true,
    content: {
      sections: [
        {
          heading: "GPS as a Vulnerability",
          text: "Modern military operations assume GPS will be contested. Jamming and spoofing are standard capabilities for near-peer adversaries. Indoor, underground, and urban environments present additional challenges where satellite signals are unavailable or unreliable."
        },
        {
          heading: "Vision as Primary Navigation",
          text: "True GPS-independent systems treat vision as the primary navigation method—not as a backup. The architecture must be designed from the ground up to operate without satellite positioning, using camera input to maintain situational awareness and guidance."
        },
        {
          heading: "Deterministic Control",
          text: "Autonomous systems in contested environments must behave predictably. Operators and commanders need to understand what the system will do before it does it. Deterministic control logic—testable, explainable, and bounded—is essential for operational trust."
        },
        {
          heading: "Platform Integration Considerations",
          text: "Vision-based guidance must integrate with diverse platforms and control interfaces. The guidance function should be separable from platform-specific implementation, allowing deployment across aerial, ground, and marine vehicles."
        }
      ],
      relatedPlatform: { name: "AI-AV", href: "/platforms/ai-av" }
    }
  },
  {
    id: "mission-orchestration-beyond-drones",
    title: "Mission Orchestration: Beyond Individual Drones",
    excerpt: "The value of aerial operations lies in mission outcomes, not in flying drones. Orchestration platforms must coordinate assets, integrate workflows, and maintain human authority across complex operational environments.",
    category: "Operations",
    date: "2025-05-28",
    readTime: "4 min read",
    icon: Radio,
    content: {
      sections: [
        {
          heading: "The Coordination Challenge",
          text: "Emergency response and protective security operations rarely involve a single drone performing a single task. Real operations require coordination: multiple assets, handoffs between operators, integration with existing systems, and clear lines of authority."
        },
        {
          heading: "Platform Agnostic by Design",
          text: "Effective orchestration cannot be locked to proprietary drone fleets. Operators need flexibility to use qualified third-party equipment, integrate existing assets, and adapt to changing procurement requirements. The orchestration layer should be independent of specific airframe configurations."
        },
        {
          heading: "Human Authority at Every Level",
          text: "Automation supports operators—it does not replace their authority. Every deployment decision, every escalation, every critical action must have a human in the loop. The system surfaces information and options; humans make decisions."
        },
        {
          heading: "Evidence and Audit",
          text: "Operations in security-sensitive environments require accountability. Every observation, decision, and action must be logged for review. Evidence must be preserved in formats suitable for legal and regulatory requirements."
        }
      ],
      relatedPlatform: { name: "Helais", href: "/platforms/helais" }
    }
  },
  {
    id: "operational-analytics-defined-outcomes",
    title: "Operational Analytics: From Data to Decisions",
    excerpt: "The value of analytics lies in better decisions, not in technology deployment. Effective implementations start with defined outcomes, clear data governance, and measurable success criteria.",
    category: "Analytics",
    date: "2025-05-20",
    readTime: "5 min read",
    icon: BarChart3,
    content: {
      sections: [
        {
          heading: "Outcome-Led Implementation",
          text: "Too many analytics projects start with technology and hope for insights. Effective implementations start with specific questions: What decision needs to be supported? What metric would change behaviour? What threshold would trigger action?"
        },
        {
          heading: "Existing Infrastructure First",
          text: "Most organisations already have cameras, sensors, and data sources. The first question should not be 'what new infrastructure do we need?' but 'what value can we extract from what we already have?' New sensors should fill defined gaps, not create generic capability."
        },
        {
          heading: "Data Governance from Day One",
          text: "Analytics projects fail when data governance is an afterthought. Retention policies, access controls, privacy requirements, and integration specifications must be defined before deployment—not discovered during operation."
        },
        {
          heading: "Bounded Pilots with Clear Criteria",
          text: "Large-scale analytics deployments should start with bounded pilots. Define what success looks like, measure against those criteria, and scale only when value is demonstrated. Open-ended 'exploration' projects rarely deliver operational value."
        }
      ],
      relatedPlatform: { name: "UrbanAnalytix", href: "https://urban-analytix.co.uk", external: true }
    }
  },
  {
    id: "evidence-based-autonomy",
    title: "Building Trust in Autonomous Systems",
    excerpt: "Trust in autonomous systems comes from evidence and discipline, not from marketing claims. Organisations deploying autonomy need clear maturity indicators, defined test boundaries, and traceable results.",
    category: "Assurance",
    date: "2025-05-12",
    readTime: "4 min read",
    icon: FileText,
    content: {
      sections: [
        {
          heading: "The Trust Deficit",
          text: "Autonomous systems face a trust problem. Marketing claims often outpace demonstrated capability. Decision-makers struggle to distinguish between proven performance and projected potential. The result is either excessive caution or misplaced confidence."
        },
        {
          heading: "Maturity Indicators",
          text: "Clear maturity indicators help organisations understand what they are buying. Research-stage capabilities have different evidence requirements than production-ready systems. Conflating the two undermines confidence in the entire portfolio."
        },
        {
          heading: "Test Boundaries and Limitations",
          text: "Every autonomous system has boundaries—conditions under which it has been tested and validated, and conditions where performance is uncertain or undefined. Honest documentation of these boundaries enables appropriate deployment decisions."
        },
        {
          heading: "Human Authority in the Loop",
          text: "Autonomous functions operate within defined bounds. Human operators retain authority over critical decisions. Systems that support human decision-making build more trust than those that claim to replace it."
        }
      ],
      relatedPlatform: { name: "Evidence & Assurance", href: "/evidence" }
    }
  },
  {
    id: "uk-counter-uas-landscape",
    title: "The UK Counter-UAS Landscape: Challenges and Opportunities",
    excerpt: "The UK faces unique counter-UAS challenges: diverse threat profiles, complex airspace, and evolving regulatory frameworks. Effective solutions require analytical rigour and integration flexibility.",
    category: "Defence",
    date: "2025-05-05",
    readTime: "6 min read",
    icon: Shield,
    content: {
      sections: [
        {
          heading: "Evolving Threat Environment",
          text: "The drone threat landscape continues to evolve. Consumer drones, modified commercial platforms, and purpose-built hostile systems present different challenges. Effective counter-UAS capability must address this diversity without over-engineering for unlikely scenarios."
        },
        {
          heading: "Regulatory and Airspace Complexity",
          text: "UK counter-UAS operations must navigate complex regulatory environments. CAA requirements, airspace restrictions, and coordination with civil aviation all constrain operational options. Solutions must be designed within these constraints, not despite them."
        },
        {
          heading: "Integration with Existing Systems",
          text: "Counter-UAS capability does not exist in isolation. Effective deployment requires integration with existing surveillance, command and control, and physical security systems. Proprietary solutions that cannot integrate create operational silos."
        },
        {
          heading: "Evidence-Based Procurement",
          text: "Counter-UAS procurement decisions should be based on quantified evidence, not vendor demonstrations. Simulation and analysis can provide the statistical foundation needed for confident investment in capability."
        }
      ],
      relatedPlatform: { name: "UAVSimNet", href: "https://c-uas.co.uk", external: true }
    }
  }
];

const categories = [
  { name: "All", value: "all" },
  { name: "Defence", value: "Defence" },
  { name: "Technology", value: "Technology" },
  { name: "Operations", value: "Operations" },
  { name: "Analytics", value: "Analytics" },
  { name: "Assurance", value: "Assurance" },
];

export default function InsightsPage() {
  const featuredInsights = insights.filter(i => i.featured);
  const regularInsights = insights.filter(i => !i.featured);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-caption mb-4">Insights</p>
            <h1 className="text-display text-foreground mb-6">
              Perspectives & Analysis
            </h1>
            <p className="text-subtitle max-w-2xl">
              Technical perspectives on operational intelligence, autonomous systems, and mission technologies from The RLK Group.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="text-caption mb-8">Featured</h2>
          <div className="grid lg:grid-cols-2 gap-6">
            {featuredInsights.map((insight, index) => (
              <motion.article
                key={insight.id}
                className="card-pillar p-8 group cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => {
                  const element = document.getElementById(insight.id);
                  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center">
                    <insight.icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="px-2 py-0.5 rounded bg-muted/50">{insight.category}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(insight.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>
                <h3 className="text-title text-foreground mb-3 group-hover:text-foreground/80 transition-colors">
                  {insight.title}
                </h3>
                <p className="text-body mb-4">{insight.excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {insight.readTime}
                  </span>
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors flex items-center gap-1">
                    Read more <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* All Articles */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="text-caption mb-8">All Insights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {insights.map((insight, index) => (
              <motion.article
                key={insight.id}
                className="card-premium p-6 group cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                onClick={() => {
                  const element = document.getElementById(insight.id);
                  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                  <span className="px-2 py-0.5 rounded bg-muted/50">{insight.category}</span>
                  <span>{insight.readTime}</span>
                </div>
                <h3 className="text-label text-foreground mb-2 group-hover:text-foreground/80 transition-colors line-clamp-2">
                  {insight.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-3">{insight.excerpt}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Article Content Sections */}
      {insights.map((insight) => (
        <section key={insight.id} id={insight.id} className="section-padding-lg border-t border-border/50">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                  <insight.icon className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1">
                    <span className="px-2 py-0.5 rounded bg-muted/50">{insight.category}</span>
                    <span>{new Date(insight.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    <span>{insight.readTime}</span>
                  </div>
                </div>
              </div>
              
              <h2 className="text-headline text-foreground mb-6">{insight.title}</h2>
              <p className="text-lg text-muted-foreground mb-10">{insight.excerpt}</p>
              
              <div className="space-y-8">
                {insight.content.sections.map((section, idx) => (
                  <div key={idx}>
                    <h3 className="text-label text-foreground mb-3">{section.heading}</h3>
                    <p className="text-body">{section.text}</p>
                  </div>
                ))}
              </div>
              
              {insight.content.relatedPlatform && (
                <div className="mt-10 pt-8 border-t border-border/50">
                  <p className="text-sm text-muted-foreground mb-3">Related Platform</p>
                  {insight.content.relatedPlatform.external ? (
                    <a 
                      href={insight.content.relatedPlatform.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary inline-flex"
                    >
                      Learn about {insight.content.relatedPlatform.name}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </a>
                  ) : (
                    <Link 
                      to={insight.content.relatedPlatform.href}
                      className="btn-secondary inline-flex"
                    >
                      Learn about {insight.content.relatedPlatform.name}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Discuss Your Requirements
            </h2>
            <p className="text-body mb-8">
              Interested in learning more about our technologies and how they address operational challenges? Get in touch.
            </p>
            <Link to="/contact" className="btn-primary">
              Contact Us
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
